/* eslint-disable @typescript-eslint/indent */
import {
    Button,
    Checkbox,
    CheckboxGroup,
    FormControl,
    FormErrorMessage,
    FormLabel,
    Input,
    Popover,
    PopoverBody,
    PopoverContent,
    PopoverTrigger,
    Select,
    Stack,
    Text,
    Textarea,
    useToast,
    VStack,
} from "@chakra-ui/react"
import { format } from "date-fns"
import { useFormik } from "formik"
import React, { FormEvent } from "react"
import { DateRange } from "react-date-range"
import "react-date-range/dist/styles.css" // main css file
import "react-date-range/dist/theme/default.css" // theme css file
import { useNavigate } from "react-router-dom"
import * as yup from "yup"
import Lazy from "yup/lib/Lazy"
import Reference from "yup/lib/Reference"
import {
    AlertTypes,
    GenerateGeneratedAlertsReportMutationVariables,
    useGenerateGeneratedAlertsReportMutation,
} from "../graphql"

type GenerateGeneratedAlertsReportFormValues =
    GenerateGeneratedAlertsReportMutationVariables["input"]

const validationSchema = yup.object<
    Record<
        keyof GenerateGeneratedAlertsReportFormValues,
        | yup.AnySchema<any, any, any>
        | Reference<unknown>
        | Lazy<any, any>
    >
>({
    label: yup.object({
        name: yup.string().required().label("Name"),
        description: yup.string().nullable().label("Description"),
    }),
    sinceDate: yup.date().required().label("Since Date"),
    untilDate: yup.date().required().label("Until Date"),
    alertTypes: yup.array().of(yup.string()).nullable().label("Alert Types"),
    resolutionStatus: yup.string().nullable().label("Resolution Status"),
})

const initialValues: GenerateGeneratedAlertsReportFormValues = {
    label: {
        name: "",
        description: "",
    },
    sinceDate: new Date(),
    untilDate: new Date(),
    alertTypes: [],
    resolutionStatus: "All",
}

const alertTypeOptions = [
    { value: AlertTypes.RestrictedEntry, label: "Restricted Entry" },
    { value: AlertTypes.InactiveReader, label: "Inactive Reader" },
    { value: AlertTypes.AllInactiveReaders, label: "All Inactive Readers" },
    { value: AlertTypes.Crowd, label: "Crowd" },
    { value: AlertTypes.UserRoleExpiry, label: "User Role Expiry" },
    { value: AlertTypes.TagBatteryLow, label: "Tag Battery Low" },
    { value: AlertTypes.IncorrectRoute, label: "Incorrect Route" },
    { value: AlertTypes.IncorrectRouteTime, label: "Incorrect Route Time" },
    { value: AlertTypes.IdleZone, label: "Idle Zone" },
    { value: AlertTypes.SOS, label: "SOS" },
    { value: AlertTypes.InactiveService, label: "Inactive Service" },
    { value: AlertTypes.AppUpdate, label: "App Update" },
    { value: AlertTypes.AwakeStatusLogExpiry, label: "Awake Status Log Expiry" },
]

export const GenerateGeneratedAlertsReportForm: React.FC = () => {
    const [{ fetching }, generateGeneratedAlertsReport] =
        useGenerateGeneratedAlertsReportMutation()

    const toast = useToast()
    const navigate = useNavigate()

    const onSubmit = async (values: GenerateGeneratedAlertsReportFormValues) => {
        const { data, error } = await generateGeneratedAlertsReport({
            input: values,
        })

        if (error) {
            return toast({
                description: error.message.replace("[GraphQL] ", ""),
                status: "error",
            })
        }

        if (data?.generateGeneratedAlertsReport) {
            navigate(
                `/reports/past/${data.generateGeneratedAlertsReport._id}`,
                { replace: true }
            )

            return
        }
    }

    const formik = useFormik<GenerateGeneratedAlertsReportFormValues>({
        initialValues,
        validationSchema,
        onSubmit,
    })

    return (
        <VStack
            as="form"
            onSubmit={(e) =>
                formik.handleSubmit(e as unknown as FormEvent<HTMLFormElement>)
            }
            w="full"
            align="stretch"
            spacing={6}
        >
            <Stack w="full" direction={{ base: "column", xl: "row" }}>
                <VStack w="full" align="stretch">
                    <FormControl
                        isInvalid={Boolean(
                            formik.touched.label?.name &&
                                formik.errors.label?.name
                        )}
                        isRequired
                    >
                        <FormLabel fontWeight="bold">Name</FormLabel>

                        <Input
                            variant="filled"
                            bgColor="grayscale.input-background"
                            placeholder="Enter name"
                            _placeholder={{ color: "grayscale.placeholer" }}
                            {...formik.getFieldProps("label.name")}
                        />

                        <FormErrorMessage>
                            {formik.errors.label?.name}
                        </FormErrorMessage>
                    </FormControl>
                    <FormControl
                        isInvalid={Boolean(
                            formik.touched.label?.description &&
                                formik.errors.label?.description
                        )}
                    >
                        <FormLabel fontWeight="bold">Description</FormLabel>

                        <Textarea
                            variant="filled"
                            bgColor="grayscale.input-background"
                            placeholder="Enter description"
                            _placeholder={{ color: "grayscale.placeholer" }}
                            {...formik.getFieldProps("label.description")}
                        />

                        <FormErrorMessage>
                            {formik.errors.label?.description}
                        </FormErrorMessage>
                    </FormControl>
                    <Popover>
                        <PopoverTrigger>
                            {formik.values.sinceDate &&
                                formik.values.untilDate && (
                                    <FormControl>
                                        <FormLabel fontWeight="bold">
                                            Date Range
                                        </FormLabel>

                                        <Input
                                            variant="filled"
                                            bgColor="grayscale.input-background"
                                            value={`${format(
                                                formik.values.sinceDate,
                                                "MMM dd, yyyy"
                                            )} - ${format(
                                                formik.values.untilDate,
                                                "MMM dd, yyyy"
                                            )}`}
                                            isReadOnly
                                        />
                                    </FormControl>
                                )}
                        </PopoverTrigger>
                        <PopoverContent w="full" _focus={{ shadow: "none" }}>
                            <PopoverBody w="full" p="0">
                                <DateRange
                                    editableDateInputs={true}
                                    onChange={(item) => {
                                        if (
                                            item?.selection.startDate &&
                                            item?.selection.endDate
                                        ) {
                                            formik.setFieldValue(
                                                "sinceDate",
                                                new Date(
                                                    item.selection.startDate
                                                )
                                            )
                                            formik.setFieldValue(
                                                "untilDate",
                                                new Date(item.selection.endDate)
                                            )
                                        }
                                    }}
                                    moveRangeOnFirstSelection={false}
                                    ranges={[
                                        {
                                            startDate: formik.values.sinceDate,
                                            endDate: formik.values.untilDate,
                                            key: "selection",
                                        },
                                    ]}
                                    maxDate={new Date()}
                                />
                            </PopoverBody>
                        </PopoverContent>
                    </Popover>
                </VStack>
                <VStack w="full" align="stretch">
                    <FormControl>
                        <FormLabel fontWeight="bold">Alert Types</FormLabel>

                        <CheckboxGroup
                            value={formik.values.alertTypes || []}
                            onChange={(values) =>
                                formik.setFieldValue("alertTypes", values)
                            }
                        >
                            <Stack spacing={2}>
                                {alertTypeOptions.map((option) => (
                                    <Checkbox
                                        key={option.value}
                                        value={option.value}
                                    >
                                        {option.label}
                                    </Checkbox>
                                ))}
                            </Stack>
                        </CheckboxGroup>
                        <Text fontSize="sm" color="gray.500" mt={2}>
                            Leave empty to include all alert types
                        </Text>
                    </FormControl>

                    <FormControl>
                        <FormLabel fontWeight="bold">
                            Resolution Status
                        </FormLabel>

                        <Select
                            variant="filled"
                            bgColor="grayscale.input-background"
                            {...formik.getFieldProps("resolutionStatus")}
                        >
                            <option value="All">All</option>
                            <option value="Resolved">Resolved</option>
                            <option value="Unresolved">Unresolved</option>
                        </Select>
                    </FormControl>

                    <Button
                        type="submit"
                        colorScheme="primary"
                        isLoading={fetching}
                    >
                        Generate
                    </Button>
                </VStack>
            </Stack>
        </VStack>
    )
}
